import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadService } from '../services/lead.service';
import { SnackBarService } from '../services/snack-bar.service';

interface FirstMeetingData {
  id?: string;
  client_name: string;
  phone_number: string;
  email: string;
  debt_info: Array<{ name: string; balance: number }>;
  assets_info: Array<{ mortgage: number }>;
  property_value: number;
  lop_needed: number;
  vehicle_model: string;
  vehicle_year: number;
  vehicle_finance_amount: number;
  bsns_name: string;
  bsns_hst: string;
  bsns_article_ins: string;
  bsns_deal: number;
  notes: string;
  terms: string;
  debtors_income?: number | string;
  spouse_income?: number | string;
  child_tax?: number | string;
  rental_income?: number | string;
  family_support?: number | string;
  any_other_income?: number | string;
  created_at?: Date;
  updated_at?: Date;
}

@Component({
  selector: 'app-firstmeeting-view',
  templateUrl: './firstmeeting-view.component.html',
  styleUrls: ['./firstmeeting-view.component.css']
})
export class FirstmeetingViewComponent implements OnInit {
  firstMeetingData: FirstMeetingData | null = null;
  isLoading = false;
  isDownloadingPDF = false;
  meetingId: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leadService: LeadService,
    private snackBarService: SnackBarService
  ) {}

  ngOnInit(): void {
    this.meetingId = this.route.snapshot.paramMap.get('id') || '';
    if (this.meetingId) {
      this.loadFirstMeetingData();
    } else {
      // If no ID, check if data was passed via navigation
      const navigation = this.router.getCurrentNavigation();
      if (navigation?.extras?.state?.data) {
        this.firstMeetingData = navigation.extras.state.data;
      } else {
        // Load static data for demonstration
        this.loadStaticData();
      }
    }
  }

  loadStaticData(): void {
    // Static data for demonstration purposes
    this.firstMeetingData = {
      id: '1',
      client_name: 'John Doe',
      phone_number: '5551234567',
      email: 'john.doe@example.com',
      debt_info: [
        { name: 'Bank of Canada', balance: 15000 },
        { name: 'TD Bank', balance: 8500 }
      ],
      assets_info: [
        { mortgage: 250000 },
        { mortgage: 50000 }
      ],
      property_value: 450000,
      lop_needed: 1,
      vehicle_model: 'Toyota Camry',
      vehicle_year: 2020,
      vehicle_finance_amount: 25000,
      bsns_name: 'Doe Consulting Inc.',
      bsns_hst: 'HST123456789',
      bsns_article_ins: 'AI-2020-001',
      bsns_deal: 50000,
      notes: 'Client is interested in debt consolidation. Follow up required.',
      terms: 'Standard terms and conditions apply.',
      created_at: new Date('2024-01-15'),
      updated_at: new Date('2024-01-20')
    };
  }

  loadFirstMeetingData(): void {
    this.isLoading = true;
    this.leadService.getFirstMeetingById(this.meetingId).subscribe({
      next: (response) => {
        // Check if response has status: false (error response from API)
        if (response.status === false) {
          console.error('API Error:', response.message);
          this.isLoading = false;
          this.snackBarService.openSnackBar(response.message || 'Form not found', 'error-class');
          this.firstMeetingData = null;
          return;
        }
        
        // Handle successful response - extract data from wrapper if present
        const data = response.data || response;
        
        // Map all fields including income fields
        this.firstMeetingData = {
          ...data,
          debtors_income: data.debtors_income || '',
          spouse_income: data.spouse_income || '',
          child_tax: data.child_tax || '',
          rental_income: data.rental_income || '',
          family_support: data.family_support || '',
          any_other_income: data.any_other_income || ''
        };
        
        console.log('Loaded first meeting data:', this.firstMeetingData);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading first meeting data:', error);
        this.isLoading = false;
        this.snackBarService.openSnackBar('Error loading first meeting', 'error-class');
        this.firstMeetingData = null;
      }
    });
  }

  editFirstMeeting(): void {
    if (this.firstMeetingData) {
      this.router.navigate(['/dashboard/firstmeeting/edit', this.meetingId]);
    }
  }

  deleteFirstMeeting(): void {
    if (confirm('Are you sure you want to delete this first meeting?')) {
      this.isLoading = true;
      this.leadService.deleteFirstMeeting(this.meetingId).subscribe({
        next: () => {
          this.snackBarService.openSnackBar('First meeting deleted successfully', 'success-class');
          this.router.navigate(['/dashboard/customer-list']);
        },
        error: (error) => {
          console.error('Error deleting first meeting:', error);
          this.snackBarService.openSnackBar('Error deleting first meeting', 'error-class');
          this.isLoading = false;
        }
      });
    }
  }

  goBack(): void {
    this.router.navigate(['/dashboard/customer-list']);
  }

  downloadPDF(): void {
    this.isDownloadingPDF = true;
    this.leadService.downloadFirstMeetingPDF(this.meetingId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `first-meeting-${this.meetingId}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.isDownloadingPDF = false;
        this.snackBarService.openSnackBar('PDF downloaded successfully', 'success-class');
      },
      error: (error) => {
        console.error('Error downloading PDF:', error);
        this.snackBarService.openSnackBar('Error downloading PDF', 'error-class');
        this.isDownloadingPDF = false;
      }
    });
  }

  printPage(): void {
    window.print();
  }

  getTotalDebt(): number {
    if (!this.firstMeetingData?.debt_info) return 0;
    return this.firstMeetingData.debt_info.reduce((sum, debt) => {
      const balance = typeof debt.balance === 'string' ? parseFloat(debt.balance) : debt.balance;
      return sum + (isNaN(balance) ? 0 : balance);
    }, 0);
  }

  getTotalMortgages(): number {
    if (!this.firstMeetingData?.assets_info) return 0;
    return this.firstMeetingData.assets_info.reduce((sum, m) => {
      const mortgage = typeof m.mortgage === 'string' ? parseFloat(m.mortgage) : m.mortgage;
      return sum + (isNaN(mortgage) ? 0 : mortgage);
    }, 0);
  }

  formatMortgageValue(value: any): string {
    if (!value) return 'N/A';
    const parsed = parseFloat(String(value));
    if (!isNaN(parsed)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(parsed);
    }
    return String(value);
  }

  getMortgageLabel(index: number): string {
    const ordinals: string[] = [
      "First", "Second", "Third", "Fourth", "Fifth",
      "Sixth", "Seventh", "Eighth", "Ninth", "Tenth",
      "Eleventh", "Twelfth", "Thirteenth", "Fourteenth", "Fifteenth",
      "Sixteenth", "Seventeenth", "Eighteenth", "Nineteenth"
    ];

    const tens: string[] = [
      "", "", "Twentieth", "Thirtieth", "Fortieth",
      "Fiftieth", "Sixtieth", "Seventieth", "Eightieth", "Ninetieth"
    ];

    const ones: string[] = [
      "", "First", "Second", "Third", "Fourth", "Fifth",
      "Sixth", "Seventh", "Eighth", "Ninth"
    ];

    const num = index + 1;

    if (num <= 19) {
      return `${ordinals[num - 1]} Mortgage`;
    }

    if (num < 100) {
      const ten = Math.floor(num / 10);
      const one = num % 10;

      if (one === 0) {
        return `${tens[ten]} Mortgage`;
      }

      return `${tens[ten].replace("ieth", "y")} ${ones[one]} Mortgage`;
    }

    if (num === 100) {
      return `Hundredth Mortgage`;
    }

    return `Mortgage ${num}`;
  }

  getTotalVehicleFinance(): number {
    if (!this.firstMeetingData?.vehicle_finance_amount) return 0;
    return this.firstMeetingData.vehicle_finance_amount;
  }

  // Helper method to safely get numeric values
  getNumericValue(value: any): number {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = parseFloat(String(value));
    return isNaN(parsed) ? 0 : parsed;
  }

  // Helper method to safely get currency values
  getCurrencyValue(value: any): string {
    const numValue = this.getNumericValue(value);
    if (numValue === 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numValue);
  }
}
